/**
 * Folded groups in the nodal view. A group whose `collapsed` is set (saved with the graph, the
 * same for everyone) is drawn as one disc, a *stand-in*: a simulated node of its own, named
 * after the group, holding its members' places as offsets from it. The members are hidden;
 * links to them are drawn to the stand-in; dragging the stand-in carries them along, and the
 * positions shared with the project stay the members' own (`positions()` puts each one at the
 * stand-in plus its offset), so unfolding brings them back where the disc went.
 *
 * Only the outermost folded group of a chain gets a stand-in: what is folded inside it is
 * hidden with it. Pure functions on plain data, so they are tested without a canvas.
 */
import type { Group } from "@protocol/Group";
import type { Node } from "@protocol/Node";
import type { SimNode } from "./types";

/** What a stand-in's id starts with (followed by the group's id). */
export const FOLD_PREFIX = "fold::";

/** The stand-in's id for a group. */
export function standInId(group: string): string {
  return FOLD_PREFIX + group;
}

/** The group a stand-in id names, or `null` for a node's id. */
export function foldedGroupOf(id: string): string | null {
  return id.startsWith(FOLD_PREFIX) ? id.slice(FOLD_PREFIX.length) : null;
}

/** What a stand-in holds. */
export interface Fold {
  /** The folded group. */
  group: Group;
  /** The nodes hidden inside it (in it or in any group inside it). */
  members: SimNode[];
  /** Each member's place from the stand-in, by node id. */
  offsets: Map<string, [number, number]>;
}

/**
 * For each group, the outermost folded group containing it (itself included), or nothing when
 * no group of its chain is folded. A parent that does not exist ends the chain; a cycle (which
 * the core refuses anyway) stops at the first group seen twice.
 */
export function foldsOf(groups: readonly Group[]): Map<string, Group> {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const out = new Map<string, Group>();
  for (const group of groups) {
    let outermost: Group | null = null;
    const seen = new Set<string>();
    for (let at: Group | undefined = group; at && !seen.has(at.id); at = at.parent ? byId.get(at.parent) : undefined) {
      seen.add(at.id);
      if (at.collapsed) outermost = at;
    }
    if (outermost) out.set(group.id, outermost);
  }
  return out;
}

/** The node a stand-in is drawn as: the group's name and note, in the group's parent. */
export function standInNode(group: Group, members: readonly SimNode[]): Node {
  // The most used first category of its members colours it.
  const counts = new Map<string, number>();
  for (const m of members) {
    const first = m.node.categories[0];
    if (first !== undefined) counts.set(first, (counts.get(first) ?? 0) + 1);
  }
  const category = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0];
  return {
    id: standInId(group.id),
    name: group.name,
    kind: "",
    categories: category === undefined ? [] : [category],
    note: group.note,
    comments: [],
    group: group.parent,
    environment: group.environment,
    workspace: group.workspace,
  };
}

/** The middle of `members`' places (the origin when there are none). */
export function centreOf(members: readonly { x: number; y: number }[]): [number, number] {
  if (!members.length) return [0, 0];
  let x = 0;
  let y = 0;
  for (const m of members) {
    x += m.x;
    y += m.y;
  }
  return [x / members.length, y / members.length];
}

/** Puts the stand-in in the middle of its members and measures their offsets from there. */
export function recentre(standIn: SimNode, fold: Fold): void {
  const [x, y] = centreOf(fold.members);
  standIn.x = x;
  standIn.y = y;
  fold.offsets.clear();
  for (const m of fold.members) fold.offsets.set(m.id, [m.x - x, m.y - y]);
}

/** Where a folded member is now: its stand-in plus its offset. */
export function memberPlace(standIn: SimNode, fold: Fold, id: string): [number, number] {
  const at = standIn.goal ?? standIn;
  const [dx, dy] = fold.offsets.get(id) ?? [0, 0];
  return [at.x + dx, at.y + dy];
}

/** A stand-in's radius: grows with how many nodes it hides, so a big folded group looks big. */
export function standInRadius(count: number): number {
  return 12 + Math.sqrt(count) * 3.2;
}
